import ChevronRight from '@mui/icons-material/ChevronRight';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DocumentStatusChip } from './DocumentStatusChip';
import { CopyLinkButton } from './CopyLinkButton';
import { useContentMetadata } from 'components/editor/hooks/useContentMetadata';
import { ContentType } from 'utils/getContentType';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateDocumentStatus } from 'hooks/useUpdateDocumentStatus.ts';
import { openNotification } from 'components/notification';
import { ViewerIdentificationTab } from './ViewerIdentificationTab';
import { DocumentExpiryDateSelector } from './DocumentExpiryDateSelector';
import PdfDownloadButtonForShareCard from './PdfDownloadButtonForShareCard.tsx';
import { AutomaticReminderSection } from './automatic-reminders/AutomaticReminderSection.tsx';
import { AutomaticReminderTab } from './automatic-reminders/AutomaticReminderTab.tsx';
import { DocumentStatus } from 'services/documents/entities.ts';

interface ShareCardV3Props {
  contentId: string;
  previewURL: string;
  documentTitleWithCompanyName: string;
  handleOpenEmailShareDialog: () => void;
}

const LOCKED_DOCUMENT_STATUSES: DocumentStatus[] = [DocumentStatus.AWAITING_SIGNATURE, DocumentStatus.WON, DocumentStatus.LOST];

export const ShareCardV3 = ({ contentId, previewURL, documentTitleWithCompanyName, handleOpenEmailShareDialog }: ShareCardV3Props) => {
  const [tab, setTab] = useState<'share' | 'viewer' | 'automatic_reminders'>('share');

  const { t } = useTranslation();

  const { data: documentData, isLoading } = useContentMetadata(ContentType.DOCUMENT, contentId);
  const { updateStatus } = useUpdateDocumentStatus({
    contentId,
    onSuccess: () => {
      openNotification({
        type: 'success',
        title: t('share_document_v3.share_card.document_status_response', { status: 'Draft' }),
      });
    },
    onError: () => {
      openNotification({
        type: 'error',
        title: t('share_document_v3.share_card.document_status_response_error', { status: 'Draft' }),
      });
    },
  });

  const documentStatus = useMemo(() => {
    return documentData?.status || DocumentStatus.DRAFT;
  }, [documentData]);

  const renderCopyByStatus = useMemo(() => {
    if (documentStatus === DocumentStatus.DRAFT) {
      return t('share_document_v3.share_card.copy_the_link_to_change_status_to_sent');
    } else if (documentStatus === DocumentStatus.LOST || documentStatus === DocumentStatus.WON) {
      return t('share_document_v3.share_card.anyone_can_view');
    } else {
      return t('share_document_v3.share_card.anyone_can_view_and_sign');
    }
  }, [documentStatus]);

  const handleMoveBackToDraft = () => {
    if (LOCKED_DOCUMENT_STATUSES.includes(documentStatus)) {
      return;
    }

    updateStatus('Draft');
  };

  return (
    <>
      <Card>
        {tab === 'share' ? (
          <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
            <Stack spacing={2} mb={1}>
              <Typography variant="h5" fontSize="24px" fontWeight={600}>
                {t('share_document_v3.share_card.share_settings')}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={2} mb={1}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body1" fontSize="16px" fontWeight={600} sx={{ lineHeight: 1 }}>
                  {t('share_document_v3.share_card.document_status')}
                </Typography>
                {isLoading ? <CircularProgress size={20} /> : <DocumentStatusChip documentStatus={documentStatus} />}
              </Stack>
              {documentStatus === DocumentStatus.SENT && (
                <Button
                  variant="text"
                  color="info"
                  size="small"
                  onClick={handleMoveBackToDraft}
                  disabled={isLoading}
                  sx={{
                    color: (theme) => theme.palette.info.main,
                  }}
                >
                  {t('share_document_v3.share_card.move_back_to_draft')}
                </Button>
              )}
            </Stack>
            <Stack gap={2}>
              <Typography variant="body2" fontSize="14px" fontWeight={400}>
                {renderCopyByStatus}
              </Typography>
              <CopyLinkButton contentId={contentId} documentStatus={documentStatus} previewURL={previewURL} />
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" color="primary" startIcon={<EmailOutlined />} onClick={handleOpenEmailShareDialog}>
                  {t('share_document_v3.share_card.share_via_email')}
                </Button>
                <PdfDownloadButtonForShareCard contentId={contentId} fileName={documentTitleWithCompanyName} />
              </Stack>
              <Divider />
              <DocumentExpiryDateSelector contentId={contentId} />
              <AutomaticReminderSection onNext={() => setTab('automatic_reminders')} />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                onClick={() => setTab('viewer')}
                sx={{ cursor: 'pointer' }}
              >
                <Stack>
                  <Typography variant="h5" fontSize="16px" fontWeight={400}>
                    {t('share_document_v3.share_card.viewer_identification')}
                  </Typography>
                  <Typography variant="body1" fontSize="16px" fontWeight={400} color="text.secondary">
                    {t('share_document_v3.share_card.email')}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="flex-end">
                  <IconButton data-testid="share-card-v3-share-settings-button">
                    <ChevronRight />
                  </IconButton>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        ) : tab === 'viewer' ? (
          <ViewerIdentificationTab onBack={() => setTab('share')} contentId={contentId} />
        ) : tab === 'automatic_reminders' ? (
          <AutomaticReminderTab onBack={() => setTab('share')} />
        ) : (
          <></>
        )}
      </Card>
    </>
  );
};
